import { AdminLayout } from "components/admin/admin-layout";
import { Button } from "components/buttons/button";
import {
  BookSelectorValue,
  BooksSelector,
} from "components/forms/book-selector";
import {
  Input,
  Label,
  StandardInput,
  ValidatedInput,
} from "components/forms/input";
import currency from "currency.js";
import gql from "graphql-tag";
import { useCreateNewDiscountMutation } from "lib/graphql-generated";
import { useInput } from "lib/hooks/use-input";
import { isFinite } from "lodash";
import { generateSlug } from "random-word-slugs";
import { useEffect } from "react";

const SAVE_DISCOUNT = gql`
  mutation CreateNewDiscount($data: discounts_insert_input!) {
    insert_discounts_one(object: $data) {
      code
    }
  }
`;

export const NewDiscountPage = () => {
  const code = useInput<string>(generateSlug(3).toUpperCase());
  const isFixed = useInput<boolean>(false);
  const amount = useInput<number>(0, (v) => {
    if (!isFinite(parseInt(v.toString()))) {
      return { valid: false, message: "Value must be a number" };
    }
    if (!isFixed.value && v > 100) {
      return { valid: false, message: "Percentages cannot go above 100%" };
    } else if (v <= 0) {
      return { valid: false, messsage: "Please enter a value higher than 0." };
    } else {
      return { valid: true };
    }
  });
  const books = useInput<BookSelectorValue[]>([]);
  const shouldLimit = useInput<boolean>(false);
  const limit = useInput<number>(0);

  useEffect(() => {
    amount.validate();
  }, [amount.value]);

  const [save] = useCreateNewDiscountMutation();
  const isSaveDisabled = !amount.isValid || code.value === "";

  const handleSave = (e) => {
    e.preventDefault();
    save({
      variables: {
        data: {
          code: code.value.trim(),
          limit_uses: shouldLimit.value ? limit.value : null,
          is_fixed_amount: isFixed.value,
          discount_value: isFixed.value
            ? currency(amount.value).intValue
            : amount.value,
          discounted_books: {
            data: books.value.map((book) => ({ book_isbn: book.data.isbn })),
          },
        },
      },
    });
  };
  return (
    <>
      <AdminLayout page='New Discount'>
        <article className='space-y-4'>
          <header className='flex flex-row justify-between'>
            <h1 className='header text-4xl'>Creating New Discount</h1>
            <section className='space-x-4'>
              <Button
                type='button'
                buttonType='primary'
                buttonSize='md'
                disabled={isSaveDisabled}
                onClick={handleSave}>
                Save
              </Button>
              <Button
                buttonType='ghost'
                buttonSize='md'
                href='/admin/discounts'>
                Cancel
              </Button>
            </section>
          </header>
          <form
            className='flex flex-row flex-wrap w-full space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-4 xl:gap-8'
            onSubmit={handleSave}>
            <StandardInput
              label='Code'
              id='code'
              name='code'
              bind={{ ...code.bind }}
              type='text'
            />
            <section className='space-y-2 flex flex-col'>
              <ValidatedInput
                showError={amount.isModified && !amount.isValid}
                errorMessage={amount.validation.message}
                label={`Amount (${
                  isFixed.value ? "£ - Fixed" : "% - Percentage"
                })`}
                id='amount'
                name='amount'
                bind={{ ...amount.bind }}
                type='number'
              />

              <Label className='flex flex-row'>
                <input
                  type='checkbox'
                  name='isFixed'
                  id='isFixed'
                  checked={isFixed.value}
                  onChange={(e) => {
                    isFixed.setValue(e.target.checked);
                  }}
                  className='h-6 w-6 border-gray-400 mr-2 rounded '
                />
                Is Fixed Amount
              </Label>
            </section>

            <section className='space-y-2 flex flex-col'>
              <Label htmlFor='limit'>
                Limit
                <Input
                  type='number'
                  name='limit'
                  id='limit'
                  {...limit.bind}
                  disabled={!shouldLimit.value}
                />
              </Label>

              <Label htmlFor='shouldLimit'>
                <input
                  type='checkbox'
                  name='shouldLimit'
                  id='shouldLimit'
                  className='h-6 w-6 border-gray-400 mr-2 rounded '
                  checked={shouldLimit.value}
                  onChange={(e) => {
                    shouldLimit.setValue(e.target.checked);
                  }}
                />
                Limit Uses
              </Label>
            </section>
            <div>
              <BooksSelector
                value={books.value}
                setValue={books.setValue}
                label='Apply to books:'
              />
              <p className='text-sm text-gray-700 pt-2'>
                NOTE: Leave books empty if you want this discount to apply to
                ALL books
              </p>
            </div>
          </form>
        </article>
      </AdminLayout>
    </>
  );
};

export default NewDiscountPage;
